/**
 * 100日クリエイティブコーディング計画のユーティリティ
 */

import { DayMetadata, Progress, PHASES } from '@/types';

// Export alias for compatibility
export const getAllDayMetadata = generateAllDaysMetadata;

/**
 * 100日間の全作品メタデータを生成
 */
export function generateAllDaysMetadata(): DayMetadata[] {
  const days: DayMetadata[] = [];
  
  // Phase 1: 基礎環境構築（Day 1-10）
  const phase1Works = [
    {
      id: 1,
      title: 'Memory',
      titleJa: '記憶',
      technologies: ['p5.js', 'GSAP'],
      description: '過去の記憶がよみがえっては消えていく抽象表現',
      interaction: 'マウスホバーで記憶の断片が現れる',
      estimatedTime: 240,
      difficulty: 1 as const
    },
    {
      id: 2,
      title: 'Wave',
      titleJa: '波動',
      technologies: ['Canvas API', 'sin/cos', 'Animation'],
      description: '複数レイヤーの正弦波による美しい波動表現',
      interaction: 'マウス位置で波の周波数とパラメータが変化',
      estimatedTime: 270,
      difficulty: 1 as const
    },
    {
      id: 3,
      title: 'Particles',
      titleJa: 'パーティクル',
      technologies: ['Canvas API', 'Physics Simulation', 'Vector2D'],
      description: '物理シミュレーションによるパーティクルシステム',
      interaction: 'クリックで新しいパーティクルを生成、重力と風の影響',
      estimatedTime: 270,
      difficulty: 2 as const
    },
    {
      id: 4,
      title: 'Fractals',
      titleJa: 'フラクタル',
      technologies: ['Canvas API', 'Recursive Algorithm'],
      description: '再帰的フラクタル図形の生成とアニメーション',
      interaction: 'マウス位置で分岐角度と深度が変化、クリックで形状切り替え',
      estimatedTime: 300,
      difficulty: 2 as const
    },
    {
      id: 5,
      title: 'Cellular Automata',
      titleJa: 'セルオートマタ',
      technologies: ['Canvas API', 'Cellular Automata Algorithm'],
      description: 'Conway\'s Game of Lifeと複数のルールセットによる進化シミュレーション',
      interaction: 'クリックでセル状態変更、スペースキーで一時停止、Rキーでリセット',
      estimatedTime: 285,
      difficulty: 2 as const
    },
    {
      id: 6,
      title: 'Sound Visualization',
      titleJa: '音響可視化',
      technologies: ['Web Audio API', 'Canvas API', 'FFT'],
      description: 'Web Audio APIを使用した音響データのリアルタイム可視化',
      interaction: 'マイクからの音声入力をリアルタイム解析、クリックで可視化モード切替',
      estimatedTime: 360,
      difficulty: 3 as const
    },
    {
      id: 7,
      title: 'L-Systems',
      titleJa: 'Lシステム',
      technologies: ['Canvas API', 'L-System Algorithm', 'Turtle Graphics'],
      description: 'Lindenmayerシステムによる自然的パターンの生成',
      interaction: 'マウス位置で角度調整、クリックでシステム切り替え、キーで世代進行',
      estimatedTime: 330,
      difficulty: 2 as const
    },
    {
      id: 8,
      title: 'Perlin Noise',
      titleJa: 'パーリンノイズ',
      technologies: ['Canvas API', 'Perlin Noise Algorithm', 'Multiple Octaves'],
      description: '自然な模様とテクスチャの生成',
      interaction: 'マウス位置でノイズパラメータ調整、クリックでモード切り替え',
      estimatedTime: 315,
      difficulty: 2 as const
    },
    {
      id: 9,
      title: 'Flow Field',
      titleJa: 'フローフィールド',
      technologies: ['Canvas API', 'Perlin Noise', 'Vector Field', 'Particle System'],
      description: 'ノイズベースのベクトル場によるパーティクルの流動',
      interaction: 'マウス位置でフローの強度調整、クリックでパーティクル追加',
      estimatedTime: 375,
      difficulty: 3 as const
    },
    {
      id: 10,
      title: 'Voronoi Diagram',
      titleJa: 'ボロノイ図',
      technologies: ['Canvas API', 'Computational Geometry', 'Voronoi Algorithm'],
      description: '最近接点に基づく平面分割とDelaunay三角形',
      interaction: 'クリックで点を追加、マウス移動でアニメーション、キーで表示モード切替',
      estimatedTime: 420,
      difficulty: 3 as const
    }
  ];

  // Phase 2: 数学的表現（Day 11-20）
  const phase2Works = [
    {
      id: 11,
      title: 'Fractal',
      titleJa: 'フラクタル',
      technologies: ['p5.js', '数学'],
      description: 'マンデルブロ集合やジュリア集合の美しさ',
      interaction: 'ズーム・パン操作で探索',
      estimatedTime: 480,
      difficulty: 2 as const
    },
    {
      id: 12,
      title: 'Spiral',
      titleJa: '螺旋',
      technologies: ['p5.js', '極座標'],
      description: '黄金比螺旋やアルキメデス螺旋の表現',
      interaction: 'パラメータで螺旋の形状変化',
      estimatedTime: 360,
      difficulty: 2 as const
    },
    {
      id: 13,
      title: 'Noise Flow',
      titleJa: 'ノイズフロー',
      technologies: ['p5.js', 'simplex-noise'],
      description: 'パーリンノイズによる自然な流れの表現',
      interaction: 'マウスでノイズの強度を調整',
      estimatedTime: 420,
      difficulty: 2 as const
    },
    {
      id: 14,
      title: 'Cellular',
      titleJa: 'セルラー',
      technologies: ['p5.js', 'セルオートマトン'],
      description: 'ライフゲームや独自ルールのセルオートマトン',
      interaction: 'クリックで初期状態をリセット',
      estimatedTime: 540,
      difficulty: 2 as const
    },
    {
      id: 15,
      title: 'Boids',
      titleJa: '群集',
      technologies: ['p5.js', '群集アルゴリズム'],
      description: '鳥の群れや魚の群れの集合行動',
      interaction: 'マウスが障害物や誘引物となる',
      estimatedTime: 600,
      difficulty: 3 as const
    },
    {
      id: 16,
      title: 'Voronoi',
      titleJa: 'ボロノイ',
      technologies: ['p5.js', 'ドロネー三角形'],
      description: 'ボロノイ図による空間分割の美しさ',
      interaction: 'クリックで新しい点を追加',
      estimatedTime: 480,
      difficulty: 2 as const
    },
    {
      id: 17,
      title: 'L-System',
      titleJa: 'Lシステム',
      technologies: ['p5.js', '文法'],
      description: '植物の成長パターンを数学的に表現',
      interaction: '世代数の調整で複雑さが変化',
      estimatedTime: 540,
      difficulty: 2 as const
    },
    {
      id: 18,
      title: 'Strange Attractor',
      titleJa: 'ストレンジアトラクター',
      technologies: ['p5.js', '微分方程式'],
      description: 'カオス理論による美しい軌道',
      interaction: 'パラメータで軌道の形状変化',
      estimatedTime: 600,
      difficulty: 3 as const
    },
    {
      id: 19,
      title: 'Reaction Diffusion',
      titleJa: '反応拡散',
      technologies: ['p5.js', '偏微分方程式'],
      description: '化学反応による自然なパターン形成',
      interaction: '異なる反応条件の切り替え',
      estimatedTime: 720,
      difficulty: 3 as const
    },
    {
      id: 20,
      title: 'Fourier',
      titleJa: 'フーリエ',
      technologies: ['p5.js', '周波数解析'],
      description: 'フーリエ変換による波形の美しさ',
      interaction: '音声入力で波形が変化',
      estimatedTime: 660,
      difficulty: 3 as const
    }
  ];

  // Phase 3: インタラクティブ体験 — 音声・物理（Day 21-30）
  const phase3Works = [
    { id: 21, title: 'Audio Spectrum', titleJa: '音響スペクトラム', technologies: ['Web Audio API', 'Canvas API', 'FFT'], description: 'マイク入力のリアルタイム周波数スペクトラム可視化', interaction: 'マイクで音を拾い、バーやリングで可視化', estimatedTime: 360, difficulty: 2 as const },
    { id: 22, title: 'Gravity Well', titleJa: '重力井戸', technologies: ['matter.js', 'Canvas API'], description: '複数の重力源に引かれるパーティクルの軌道シミュレーション', interaction: 'クリックで重力源を配置、ドラッグで質量調整', estimatedTime: 420, difficulty: 2 as const },
    { id: 23, title: 'Cymatics', titleJa: 'サイマティクス', technologies: ['Web Audio API', 'Canvas API', 'Wave Equation'], description: '音波による砂粒パターンの物理シミュレーション', interaction: '周波数スライダーでパターン変化、音声入力対応', estimatedTime: 480, difficulty: 3 as const },
    { id: 24, title: 'Cloth Simulation', titleJa: '布シミュレーション', technologies: ['Canvas API', 'Verlet Integration'], description: 'バネ質点モデルによるリアルタイム布の物理挙動', interaction: 'ドラッグで布をつかむ、風のパラメータ調整', estimatedTime: 540, difficulty: 3 as const },
    { id: 25, title: 'Pendulum Waves', titleJa: '振り子波', technologies: ['Canvas API', 'Harmonic Motion'], description: '異なる周期の振り子群が生む美しい波のパターン', interaction: '振り子の数と周期差をリアルタイム調整', estimatedTime: 360, difficulty: 1 as const },
    { id: 26, title: 'Synesthesia', titleJa: '共感覚', technologies: ['Web Audio API', 'WebGL', 'GLSL'], description: '音の高さ・音量・音色を色・形・動きに変換する共感覚体験', interaction: '音声入力でリアルタイムに映像が変化', estimatedTime: 600, difficulty: 3 as const },
    { id: 27, title: 'Spring Network', titleJa: 'バネネットワーク', technologies: ['Canvas API', 'Spring Physics'], description: '相互に接続されたバネ質点ネットワークの動的挙動', interaction: 'ノードをドラッグすると波紋が伝播', estimatedTime: 420, difficulty: 2 as const },
    { id: 28, title: 'Fluid Ink', titleJa: '流体インク', technologies: ['WebGL', 'Navier-Stokes'], description: '水面にインクを落としたような流体力学シミュレーション', interaction: 'マウスドラッグでインクを注入、色と粘度を調整', estimatedTime: 600, difficulty: 3 as const },
    { id: 29, title: 'Sound Garden', titleJa: '音の庭', technologies: ['tone.js', 'p5.js', 'Generative Music'], description: '植物が成長しながら音を奏でるジェネレーティブ音楽体験', interaction: 'クリックで種を植え、成長とともに音色が変化', estimatedTime: 540, difficulty: 2 as const },
    { id: 30, title: 'Collision Orchestra', titleJa: '衝突オーケストラ', technologies: ['matter.js', 'tone.js', 'Canvas API'], description: '物体の衝突が音を生む物理×音楽のインタラクション', interaction: '物体を投入すると衝突で和音が生まれる', estimatedTime: 480, difficulty: 2 as const },
  ];

  // Phase 4: 3D表現（Day 31-40）
  const phase4Works = [
    { id: 31, title: 'Wireframe World', titleJa: 'ワイヤーフレーム世界', technologies: ['Three.js', 'R3F'], description: 'ワイヤーフレームだけで構成された幾何学的な都市景観', interaction: 'WASD移動、マウスで視点操作', estimatedTime: 480, difficulty: 2 as const },
    { id: 32, title: 'Shader Playground', titleJa: 'シェーダー実験場', technologies: ['Three.js', 'GLSL', 'ShaderMaterial'], description: 'カスタムシェーダーによる波紋・ディストーション・色彩効果', interaction: 'パラメータでシェーダーをリアルタイム調整', estimatedTime: 540, difficulty: 3 as const },
    { id: 33, title: 'Instanced Forest', titleJa: 'インスタンス森林', technologies: ['Three.js', 'InstancedMesh', 'Wind Simulation'], description: '10万本の木をインスタンスレンダリングで描画する森林', interaction: '風の方向と強度を変更、四季切り替え', estimatedTime: 600, difficulty: 3 as const },
    { id: 34, title: 'Morphing Geometry', titleJa: '変形ジオメトリ', technologies: ['Three.js', 'R3F', 'Morph Targets'], description: '球体→立方体→トーラスなど形状間のスムーズモーフィング', interaction: 'スライダーでモーフ率を操作', estimatedTime: 420, difficulty: 2 as const },
    { id: 35, title: 'Raymarching', titleJa: 'レイマーチング', technologies: ['Three.js', 'GLSL', 'SDF'], description: 'Signed Distance Fieldによるリアルタイムレイマーチング', interaction: 'マウスでカメラ回転、パラメータで形状変化', estimatedTime: 600, difficulty: 3 as const },
    { id: 36, title: 'Particle Galaxy', titleJa: 'パーティクル銀河', technologies: ['Three.js', 'BufferGeometry', 'Points'], description: '50万パーティクルで構成される渦巻銀河のシミュレーション', interaction: '回転速度と視点を操作、銀河衝突モード', estimatedTime: 540, difficulty: 2 as const },
    { id: 37, title: 'Post Processing', titleJa: 'ポストプロセス', technologies: ['Three.js', 'postprocessing', 'EffectComposer'], description: 'ブルーム、グリッチ、色収差などのポストエフェクト実験', interaction: 'エフェクトの組み合わせとパラメータ調整', estimatedTime: 480, difficulty: 2 as const },
    { id: 38, title: 'Terrain Generator', titleJa: '地形生成', technologies: ['Three.js', 'Perlin Noise', 'LOD'], description: 'プロシージャル地形生成とリアルタイムLOD切り替え', interaction: 'シード値変更で無限の地形、飛行モード', estimatedTime: 600, difficulty: 3 as const },
    { id: 39, title: 'PBR Materials', titleJa: 'PBRマテリアル', technologies: ['Three.js', 'PBR', 'Environment Map'], description: '金属・ガラス・木材など物理ベースマテリアルのショーケース', interaction: 'ラフネス・メタリック値をリアルタイム調整', estimatedTime: 420, difficulty: 2 as const },
    { id: 40, title: 'Portal', titleJa: 'ポータル', technologies: ['Three.js', 'R3F', 'Stencil Buffer'], description: 'ステンシルバッファで異世界を覗くポータルエフェクト', interaction: 'ポータルに近づくと異なるシーンが見える', estimatedTime: 600, difficulty: 3 as const },
  ];

  // Phase 5: 複雑パターン — 高度アルゴリズム（Day 41-50）
  const phase5Works = [
    { id: 41, title: 'Slime Mold', titleJa: '粘菌シミュレーション', technologies: ['WebGL', 'Compute Shader', 'Agent-Based'], description: '粘菌の知性を模した100万エージェントのネットワーク形成', interaction: '食料源を配置して粘菌の経路探索を観察', estimatedTime: 600, difficulty: 3 as const },
    { id: 42, title: 'Diffusion Limited Aggregation', titleJa: '拡散律速凝集', technologies: ['Canvas API', 'DLA Algorithm'], description: '結晶成長や雷の分岐パターンを再現するDLAシミュレーション', interaction: 'シード位置と粒子密度を調整', estimatedTime: 480, difficulty: 2 as const },
    { id: 43, title: 'Fluid Dynamics', titleJa: '流体力学', technologies: ['WebGL', 'Navier-Stokes', 'Jacobi Iteration'], description: 'GPU加速のリアルタイム2D流体シミュレーション', interaction: 'マウスで渦を生成、障害物を配置', estimatedTime: 720, difficulty: 3 as const },
    { id: 44, title: 'Wave Equation', titleJa: '波動方程式', technologies: ['WebGL', 'GLSL', 'Wave Simulation'], description: '2次元波動方程式のGPU並列計算と干渉パターン', interaction: 'クリックで波源を追加、壁を描いて反射を観察', estimatedTime: 540, difficulty: 3 as const },
    { id: 45, title: 'Genetic Art', titleJa: '遺伝的アート', technologies: ['Canvas API', 'Genetic Algorithm'], description: '遺伝的アルゴリズムで写真を三角形の集合に進化させる', interaction: '目標画像をアップロード、世代の進化を観察', estimatedTime: 480, difficulty: 2 as const },
    { id: 46, title: 'Marching Cubes', titleJa: 'マーチングキューブ', technologies: ['Three.js', 'Marching Cubes', 'Metaball'], description: 'メタボールのリアルタイムポリゴン化と有機的形状生成', interaction: 'メタボール追加・移動でリアルタイム変形', estimatedTime: 600, difficulty: 3 as const },
    { id: 47, title: 'Flocking 3D', titleJa: '3D群衆行動', technologies: ['Three.js', 'Boids', 'InstancedMesh'], description: '3次元空間での群衆行動アルゴリズムと魚群表現', interaction: '捕食者を操作して群れの反応を観察', estimatedTime: 540, difficulty: 2 as const },
    { id: 48, title: 'Erosion Simulation', titleJa: '浸食シミュレーション', technologies: ['Three.js', 'Hydraulic Erosion'], description: '水力浸食アルゴリズムによるリアルな地形変化', interaction: '雨量と地質パラメータで浸食パターンが変化', estimatedTime: 600, difficulty: 3 as const },
    { id: 49, title: 'Magnetic Field', titleJa: '磁場可視化', technologies: ['Canvas API', 'Vector Field', 'Iron Filing'], description: '磁石の配置による磁力線と鉄粉パターンのシミュレーション', interaction: '磁石をドラッグして磁場パターンを操作', estimatedTime: 420, difficulty: 2 as const },
    { id: 50, title: 'Neural Style Transfer', titleJa: 'ニューラルスタイル変換', technologies: ['TensorFlow.js', 'WebGL', 'CNN'], description: 'リアルタイムのニューラルスタイル変換をカメラ映像に適用', interaction: 'スタイル画像の選択と強度調整', estimatedTime: 720, difficulty: 3 as const },
  ];

  // Phase 6: 動的演出 — サイバー表現（Day 51-60）
  const phase6Works = [
    { id: 51, title: 'Glitch Art', titleJa: 'グリッチアート', technologies: ['WebGL', 'GLSL', 'Data Moshing'], description: 'デジタルグリッチとデータモッシュのリアルタイムエフェクト', interaction: 'グリッチの強度・種類を操作、画像アップロード', estimatedTime: 420, difficulty: 2 as const },
    { id: 52, title: 'Holographic UI', titleJa: 'ホログラフィックUI', technologies: ['Three.js', 'R3F', 'GSAP'], description: '攻殻機動隊風の浮遊するホログラフィックインターフェース', interaction: 'UI要素をドラッグで配置、情報パネルの展開', estimatedTime: 600, difficulty: 3 as const },
    { id: 53, title: 'Data Rain', titleJa: 'データレイン', technologies: ['Canvas API', 'Typography'], description: 'マトリクス風のカタカナ・漢字の雨が降り注ぐ演出', interaction: 'マウス位置で雨の密度と速度が変化', estimatedTime: 360, difficulty: 1 as const },
    { id: 54, title: 'Circuit Board', titleJa: '回路基板', technologies: ['Canvas API', 'Maze Algorithm'], description: 'プロシージャルに生成される回路基板パターンとパルス信号', interaction: '信号の流れをリアルタイムで追跡、ルーティング変更', estimatedTime: 480, difficulty: 2 as const },
    { id: 55, title: 'Cyber Tunnel', titleJa: 'サイバートンネル', technologies: ['Three.js', 'GLSL', 'Tube Geometry'], description: 'トロンのようなネオンが輝く無限サイバートンネル走行', interaction: '速度と色のテーマを変更', estimatedTime: 420, difficulty: 2 as const },
    { id: 56, title: 'Typing Matrix', titleJa: 'タイピングマトリクス', technologies: ['Canvas API', 'GSAP', 'Typography'], description: 'タイプされた文字がパーティクル化して飛散・再構成される演出', interaction: 'キーボード入力でリアルタイムに文字がパーティクル化', estimatedTime: 480, difficulty: 2 as const },
    { id: 57, title: 'Scan Effect', titleJa: 'スキャンエフェクト', technologies: ['Three.js', 'GLSL', 'Post Processing'], description: 'シーン全体にスキャンラインとCRTエフェクトを適用', interaction: 'エフェクト強度とスキャン速度の調整', estimatedTime: 360, difficulty: 1 as const },
    { id: 58, title: 'Network Topology', titleJa: 'ネットワークトポロジー', technologies: ['Three.js', 'Force-Directed Graph'], description: 'リアルタイムのネットワーク通信を3Dグラフで可視化', interaction: 'ノード追加・削除、通信パケットのアニメーション', estimatedTime: 540, difficulty: 3 as const },
    { id: 59, title: 'Displacement Map', titleJa: 'ディスプレイスメント', technologies: ['Three.js', 'GLSL', 'Noise'], description: 'ノイズベースの頂点ディスプレイスメントによる有機的表面', interaction: 'ノイズパラメータとスケールのリアルタイム調整', estimatedTime: 420, difficulty: 2 as const },
    { id: 60, title: 'Cyberpunk City', titleJa: 'サイバーパンク都市', technologies: ['Three.js', 'R3F', 'Instancing', 'Bloom'], description: 'ネオンが輝くサイバーパンク都市のプロシージャル生成', interaction: '都市を飛行、天候・時間帯の変更', estimatedTime: 720, difficulty: 3 as const },
  ];

  // Phase 7: 新技術実験 — AR/VR・先端技術（Day 61-70）
  const phase7Works = [
    { id: 61, title: 'Hand Tracking', titleJa: 'ハンドトラッキング', technologies: ['MediaPipe', 'Canvas API', 'ML'], description: 'カメラの手認識でパーティクルを操作するハンドトラッキング', interaction: '手のジェスチャーで魔法のようにパーティクルを操作', estimatedTime: 600, difficulty: 3 as const },
    { id: 62, title: 'Face Mesh', titleJa: 'フェイスメッシュ', technologies: ['MediaPipe', 'Three.js', 'Face Detection'], description: '顔認識メッシュ上にサイバーパンク風マスクを投影', interaction: '表情でマスクのパターンが変化', estimatedTime: 540, difficulty: 3 as const },
    { id: 63, title: 'Depth Map', titleJa: '深度マップ', technologies: ['TensorFlow.js', 'Three.js', 'Depth Estimation'], description: '単眼カメラからの深度推定と3Dポイントクラウド再構成', interaction: 'カメラ映像からリアルタイムに3D空間を生成', estimatedTime: 600, difficulty: 3 as const },
    { id: 64, title: 'Voice Visualizer', titleJa: '音声可視化装置', technologies: ['Web Speech API', 'Three.js', 'NLP'], description: '音声認識テキストが3D空間にタイポグラフィとして出現', interaction: '話しかけると言葉が立体文字になって浮遊', estimatedTime: 540, difficulty: 2 as const },
    { id: 65, title: 'Motion Capture', titleJa: 'モーションキャプチャ', technologies: ['MediaPipe Pose', 'Three.js', 'Skeleton'], description: 'カメラベースのボディトラッキングで3Dキャラクターを操作', interaction: '体の動きがリアルタイムでアバターに反映', estimatedTime: 720, difficulty: 3 as const },
    { id: 66, title: 'Spatial Audio', titleJa: '空間オーディオ', technologies: ['Web Audio API', 'Three.js', 'HRTF'], description: '3D空間内の音源配置と頭部伝達関数による立体音響', interaction: '音源を3D空間にドラッグ配置、リスナー位置を移動', estimatedTime: 540, difficulty: 2 as const },
    { id: 67, title: 'Gesture Drawing', titleJa: 'ジェスチャードローイング', technologies: ['MediaPipe', 'Canvas API', 'Bezier'], description: '手の動きの軌跡が美しい光の線として空間に描かれる', interaction: '手を動かして光のドローイング、両手で異なる色', estimatedTime: 480, difficulty: 2 as const },
    { id: 68, title: 'Object Detection Art', titleJa: '物体認識アート', technologies: ['TensorFlow.js', 'COCO-SSD', 'Canvas'], description: 'カメラ映像の物体認識結果をアート的に再構成', interaction: '認識された物体がアブストラクトな形に変換', estimatedTime: 480, difficulty: 2 as const },
    { id: 69, title: 'Emotion Color', titleJa: '感情の色', technologies: ['Face-API.js', 'Canvas API', 'Color Theory'], description: '表情認識から感情を読み取り、対応する色彩空間を生成', interaction: '表情の変化がリアルタイムに色彩パターンに反映', estimatedTime: 540, difficulty: 2 as const },
    { id: 70, title: 'AR Graffiti', titleJa: 'ARグラフィティ', technologies: ['WebXR', 'Three.js', 'AR'], description: '現実空間にバーチャルなグラフィティを描くAR体験', interaction: 'タッチでAR空間にネオンペイント', estimatedTime: 600, difficulty: 3 as const },
  ];

  // Phase 8: 数学的美 — 抽象表現（Day 71-80）
  const phase8Works = [
    { id: 71, title: 'Fibonacci Spiral', titleJa: 'フィボナッチ螺旋', technologies: ['Canvas API', 'Golden Ratio'], description: 'フィボナッチ数列と黄金比による自然界のパターン', interaction: '世代数とスケールを調整して探索', estimatedTime: 360, difficulty: 1 as const },
    { id: 72, title: 'Hyperbolic Geometry', titleJa: '双曲幾何学', technologies: ['Canvas API', 'Poincare Disk'], description: 'ポアンカレ円板上のエッシャー風タイリングパターン', interaction: 'タイルの形状と分割数を変更', estimatedTime: 600, difficulty: 3 as const },
    { id: 73, title: '4D Tesseract', titleJa: '4次元超立方体', technologies: ['Three.js', '4D Projection'], description: '4次元超立方体の3次元投影とアニメーション', interaction: '4D回転のパラメータを操作', estimatedTime: 540, difficulty: 3 as const },
    { id: 74, title: 'Penrose Tiling', titleJa: 'ペンローズタイル', technologies: ['Canvas API', 'Aperiodic Tiling'], description: '非周期的ペンローズタイリングの生成と探索', interaction: 'ズームで自己相似パターンを探索', estimatedTime: 480, difficulty: 2 as const },
    { id: 75, title: 'Moiré Pattern', titleJa: 'モアレパターン', technologies: ['Canvas API', 'Interference'], description: '重なる幾何学パターンが生む動的モアレ干渉', interaction: 'パターンの回転と間隔を調整', estimatedTime: 360, difficulty: 1 as const },
    { id: 76, title: 'Minimal Surface', titleJa: '極小曲面', technologies: ['Three.js', 'Parametric Surface'], description: 'コスタ曲面やシュヴァルツ曲面など数学的極小曲面の可視化', interaction: '曲面の種類切り替え、パラメータ変更', estimatedTime: 540, difficulty: 2 as const },
    { id: 77, title: 'Lorenz Butterfly', titleJa: 'ローレンツ蝶', technologies: ['Three.js', 'ODE Solver'], description: 'ローレンツアトラクターの軌道と初期値鋭敏性の可視化', interaction: '初期値の微小変化で軌道が分岐する様子を観察', estimatedTime: 480, difficulty: 2 as const },
    { id: 78, title: 'Klein Bottle', titleJa: 'クラインの壺', technologies: ['Three.js', 'Parametric Geometry'], description: 'クラインの壺とメビウスの帯のインタラクティブ可視化', interaction: 'カメラで多角度から観察、断面表示', estimatedTime: 420, difficulty: 2 as const },
    { id: 79, title: 'Mandelbulb', titleJa: 'マンデルバルブ', technologies: ['Three.js', 'GLSL', 'Raymarching'], description: '3次元マンデルブロ集合のGPUレイマーチング描画', interaction: 'ズームとパワー値の変更で構造探索', estimatedTime: 720, difficulty: 3 as const },
    { id: 80, title: 'Cellular Symmetry', titleJa: '細胞対称性', technologies: ['Canvas API', 'Symmetry Group'], description: '対称群に基づく万華鏡パターンの動的生成', interaction: '対称性の次数と描画パラメータを操作', estimatedTime: 420, difficulty: 2 as const },
  ];

  // Phase 9: 実験的統合 — AI×Creative Coding（Day 81-90）
  const phase9Works = [
    { id: 81, title: 'GAN Landscape', titleJa: 'GAN風景', technologies: ['TensorFlow.js', 'Canvas API', 'StyleGAN'], description: 'GANで生成した風景画をリアルタイムに補間する体験', interaction: '潜在空間上のスライダーで風景が連続変化', estimatedTime: 600, difficulty: 3 as const },
    { id: 82, title: 'Evolutionary Art', titleJa: '進化的アート', technologies: ['Canvas API', 'Genetic Algorithm', 'Fitness'], description: 'ユーザーの好みを学習する対話型進化的アート生成', interaction: '好きな作品を選択すると次世代が進化', estimatedTime: 540, difficulty: 2 as const },
    { id: 83, title: 'Text to 3D', titleJa: 'テキストから3D', technologies: ['Three.js', 'NLP', 'Procedural'], description: 'テキスト入力から3Dシーンをプロシージャル生成', interaction: '文章を入力すると対応する3Dシーンが構築', estimatedTime: 720, difficulty: 3 as const },
    { id: 84, title: 'Music Visualizer AI', titleJa: 'AI音楽可視化', technologies: ['TensorFlow.js', 'Web Audio', 'Three.js'], description: '音楽のジャンル・ムードをAIが分析し映像を自動生成', interaction: '音楽ファイルをドロップするとVJ映像が生成', estimatedTime: 600, difficulty: 3 as const },
    { id: 85, title: 'Dream Machine', titleJa: 'ドリームマシン', technologies: ['Canvas API', 'Noise', 'Color Theory'], description: 'DeepDream風の再帰的パターン増幅によるサイケデリック映像', interaction: '増幅レベルとフィルタの組み合わせを操作', estimatedTime: 480, difficulty: 2 as const },
    { id: 86, title: 'Pose Art', titleJa: 'ポーズアート', technologies: ['MediaPipe', 'Canvas API', 'Generative'], description: '全身ポーズ認識の骨格データから抽象アートを自動生成', interaction: '体を動かすとリアルタイムにアートが変化', estimatedTime: 540, difficulty: 2 as const },
    { id: 87, title: 'Swarm Intelligence', titleJa: '群知能', technologies: ['Canvas API', 'PSO', 'ACO'], description: 'アリのフェロモンや蜂のダンスなど群知能アルゴリズムの可視化', interaction: '食料源を配置して群れの最適化を観察', estimatedTime: 480, difficulty: 2 as const },
    { id: 88, title: 'Procedural Music', titleJa: 'プロシージャル音楽', technologies: ['tone.js', 'Markov Chain', 'Canvas API'], description: 'マルコフ連鎖によるジェネレーティブ音楽と連動した映像', interaction: '音楽パラメータで旋律・リズム・和声を調整', estimatedTime: 600, difficulty: 3 as const },
    { id: 89, title: 'Data Sculpture', titleJa: 'データ彫刻', technologies: ['Three.js', 'D3.js', 'Data Visualization'], description: '実データを3Dの彫刻的表現に変換するデータアート', interaction: 'データセットの切り替え、表示次元の変更', estimatedTime: 540, difficulty: 2 as const },
    { id: 90, title: 'Living Painting', titleJa: '生きた絵画', technologies: ['WebGL', 'Fluid Simulation', 'Style Transfer'], description: '名画が呼吸するように動く生きた絵画の再現', interaction: '絵画の選択、アニメーション速度と効果の調整', estimatedTime: 600, difficulty: 3 as const },
  ];

  // Phase 10: 集大成 — 総合作品（Day 91-100）
  const phase10Works = [
    { id: 91, title: 'Digital Twin City', titleJa: 'デジタルツイン都市', technologies: ['Three.js', 'R3F', 'OpenStreetMap'], description: '実在の都市データから3Dサイバーパンク都市を再構成', interaction: '飛行探索、建物の情報表示、時間帯変更', estimatedTime: 720, difficulty: 3 as const },
    { id: 92, title: 'Generative Identity', titleJa: '生成的アイデンティティ', technologies: ['Canvas API', 'Hash', 'SVG'], description: '名前や文字列から一意のビジュアルアイデンティティを生成', interaction: '名前を入力するとユニークなアートが生成', estimatedTime: 420, difficulty: 1 as const },
    { id: 93, title: 'Interactive Documentary', titleJa: 'インタラクティブドキュメンタリー', technologies: ['Three.js', 'ScrollTrigger', 'GSAP'], description: 'スクロールで進行するWebGL3Dドキュメンタリー体験', interaction: 'スクロールで物語が進行、分岐する選択肢', estimatedTime: 720, difficulty: 3 as const },
    { id: 94, title: 'Ecosystem', titleJa: 'エコシステム', technologies: ['Three.js', 'Agent-Based', 'Neural Network'], description: '捕食者-被食者の生態系シミュレーションと進化', interaction: '種の追加・環境パラメータの変更で生態系が変化', estimatedTime: 720, difficulty: 3 as const },
    { id: 95, title: 'Music of Spheres', titleJa: '天球の音楽', technologies: ['Three.js', 'tone.js', 'Astronomy'], description: '太陽系の公転周期を音楽に変換する天球の音楽', interaction: '惑星選択で和音が変化、時間加速スライダー', estimatedTime: 600, difficulty: 2 as const },
    { id: 96, title: 'Neural Garden', titleJa: 'ニューラルガーデン', technologies: ['Three.js', 'Neural Network', 'L-System'], description: 'ニューラルネットワークが育てる3D植物園', interaction: '環境パラメータで植物の成長パターンが変化', estimatedTime: 600, difficulty: 3 as const },
    { id: 97, title: 'Code Cathedral', titleJa: 'コード大聖堂', technologies: ['Three.js', 'R3F', 'Procedural Architecture'], description: 'ソースコードの構造から大聖堂のような建築物を生成', interaction: 'リポジトリURLを入力するとコードの大聖堂が出現', estimatedTime: 720, difficulty: 3 as const },
    { id: 98, title: 'Memories', titleJa: '記憶の宮殿', technologies: ['Three.js', 'Photo API', 'Spatial UI'], description: '写真がフローティングする3D記憶の宮殿', interaction: '写真をアップロードして3D空間に配置', estimatedTime: 540, difficulty: 2 as const },
    { id: 99, title: 'Creative Coding Museum', titleJa: 'クリエイティブコーディング美術館', technologies: ['Three.js', 'R3F', 'VR'], description: '全100作品を展示するバーチャル美術館', interaction: '歩き回って全作品を鑑賞、VRモード対応', estimatedTime: 720, difficulty: 3 as const },
    { id: 100, title: 'The Net', titleJa: 'ネットの海', technologies: ['Three.js', 'R3F', 'All Technologies'], description: '攻殻機動隊の「電脳空間」を再現した総合作品。全技術を統合した集大成', interaction: '電脳ダイブで情報の海を遊泳、過去99作品へのポータル', estimatedTime: 960, difficulty: 3 as const },
  ];

  // すべての Phase の作品を生成
  const allPhaseWorks = [
    ...phase1Works,
    ...phase2Works,
    ...phase3Works,
    ...phase4Works,
    ...phase5Works,
    ...phase6Works,
    ...phase7Works,
    ...phase8Works,
    ...phase9Works,
    ...phase10Works,
  ];

  // DayMetadata形式に変換
  allPhaseWorks.forEach(work => {
    const phaseNum = Math.ceil(work.id / 10);
    const dayMetadata: DayMetadata = {
      ...work,
      phase: phaseNum,
      snsHashtags: [`#100DaysOfCode`, `#Day${work.id.toString().padStart(2, '0')}`, `#${work.title.replace(/\s+/g, '')}`],
      status: work.id <= 10 ? 'completed' : (work.id <= 20 ? 'pending' : 'pending'),
      publishedAt: work.id <= 10 ? new Date(2025, 0, work.id).toISOString() : undefined,
      url: work.id <= 10 ? `/day/${work.id}` : undefined
    };
    days.push(dayMetadata);
  });

  return days;
}

/**
 * 特定の日の作品メタデータを取得
 */
export function getDayMetadata(day: number): DayMetadata | null {
  const allDays = generateAllDaysMetadata();
  return allDays.find(d => d.id === day) || null;
}

/**
 * 特定のフェーズの作品を取得
 */
export function getPhaseWorks(phase: number): DayMetadata[] {
  const allDays = generateAllDaysMetadata();
  return allDays.filter(d => d.phase === phase);
}

/**
 * 現在の進捗を計算
 */
export function calculateProgress(works: DayMetadata[]): Progress {
  const completedWorks = works.filter(w => w.status === 'completed');
  const inProgressWorks = works.filter(w => w.status === 'in_progress');
  
  const completedDays = completedWorks.length;
  const currentDay = Math.max(
    ...completedWorks.map(w => w.id),
    ...inProgressWorks.map(w => w.id),
    1
  );
  
  const progressPercentage = (completedDays / 100) * 100;
  
  // フェーズ別進捗を計算
  const phaseProgress: Record<number, number> = {};
  Object.keys(PHASES).forEach(phaseId => {
    const phaseNum = parseInt(phaseId);
    const phaseWorks = works.filter(w => w.phase === phaseNum);
    const phaseCompleted = phaseWorks.filter(w => w.status === 'completed').length;
    phaseProgress[phaseNum] = (phaseCompleted / 10) * 100;
  });
  
  return {
    completedDays,
    currentDay,
    progressPercentage,
    phaseProgress
  };
}

/**
 * 作品をフィルタリング
 */
export function filterWorks(
  works: DayMetadata[],
  filters: {
    phase?: number;
    technology?: string;
    difficulty?: 1 | 2 | 3;
    status?: 'pending' | 'in_progress' | 'completed';
    searchQuery?: string;
  }
): DayMetadata[] {
  let filtered = works;
  
  if (filters.phase) {
    filtered = filtered.filter(w => w.phase === filters.phase);
  }
  
  if (filters.technology) {
    filtered = filtered.filter(w => 
      w.technologies.some(tech => 
        tech.toLowerCase().includes(filters.technology!.toLowerCase())
      )
    );
  }
  
  if (filters.difficulty) {
    filtered = filtered.filter(w => w.difficulty === filters.difficulty);
  }
  
  if (filters.status) {
    filtered = filtered.filter(w => w.status === filters.status);
  }
  
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter(w => 
      w.title.toLowerCase().includes(query) ||
      w.titleJa.toLowerCase().includes(query) ||
      w.description.toLowerCase().includes(query)
    );
  }
  
  return filtered;
}

/**
 * 作品をソート
 */
export function sortWorks(
  works: DayMetadata[],
  sortBy: 'day' | 'title' | 'difficulty' | 'publishedAt',
  order: 'asc' | 'desc' = 'asc'
): DayMetadata[] {
  const sorted = [...works].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'day':
        comparison = a.id - b.id;
        break;
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'difficulty':
        comparison = a.difficulty - b.difficulty;
        break;
      case 'publishedAt': {
        const aDate = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const bDate = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        comparison = aDate - bDate;
        break;
      }
    }
    
    return order === 'asc' ? comparison : -comparison;
  });
  
  return sorted;
}

/**
 * 前後の作品を取得
 */
export function getAdjacentWorks(currentDay: number): {
  previous: DayMetadata | null;
  next: DayMetadata | null;
} {
  const allDays = generateAllDaysMetadata();
  
  const previous = allDays.find(d => d.id === currentDay - 1) || null;
  const next = allDays.find(d => d.id === currentDay + 1) || null;
  
  return { previous, next };
}

/**
 * 統計情報を取得
 */
export function getStatistics(works: DayMetadata[]): {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  totalEstimatedTime: number;
  averageDifficulty: number;
  technologiesUsed: string[];
} {
  const completed = works.filter(w => w.status === 'completed').length;
  const inProgress = works.filter(w => w.status === 'in_progress').length;
  const pending = works.filter(w => w.status === 'pending').length;
  
  const totalEstimatedTime = works.reduce((sum, w) => sum + w.estimatedTime, 0);
  const averageDifficulty = works.reduce((sum, w) => sum + w.difficulty, 0) / works.length;
  
  const technologiesUsed = Array.from(
    new Set(works.flatMap(w => w.technologies))
  ).sort();
  
  return {
    total: works.length,
    completed,
    inProgress,
    pending,
    totalEstimatedTime,
    averageDifficulty,
    technologiesUsed
  };
}

/**
 * 作品の実装テンプレートを生成
 */
export function generateWorkTemplate(day: number): string {
  const metadata = getDayMetadata(day);
  if (!metadata) return '';
  
  return `/**
 * Day ${day.toString().padStart(2, '0')}: ${metadata.title} (${metadata.titleJa})
 * ${metadata.description}
 * 
 * Technologies: ${metadata.technologies.join(', ')}
 * Difficulty: ${metadata.difficulty}/3
 * Estimated Time: ${Math.floor(metadata.estimatedTime / 60)}h ${metadata.estimatedTime % 60}m
 * 
 * Interaction: ${metadata.interaction}
 */

import React, { useEffect, useRef } from 'react';
import { DayWorkProps } from '@/types';

export default function Day${day.toString().padStart(2, '0')}${metadata.title.replace(/\s+/g, '')}({
  day,
  metadata,
  enableInteraction = true,
  isPreview = false,
  width = 800,
  height = 600
}: DayWorkProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Artwork implementation placeholder
    // ${metadata.description}
    
    // Setup canvas
    canvas.width = width;
    canvas.height = height;
    
    // Animation loop
    let animationId: number;
    
    const animate = () => {
      // Clear canvas
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      
      // Artwork rendering implementation goes here
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Cleanup
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [width, height, enableInteraction, isPreview]);
  
  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
      style={{ maxWidth: width, maxHeight: height }}
    />
  );
}`;
}